import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  Grid,
  Typography,
  Box,
  Checkbox,
  Button,
  TextField,
  IconButton,
} from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FilterListIcon from '@mui/icons-material/FilterList'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import SendIcon from '@mui/icons-material/Send'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import DeleteIcon from '@mui/icons-material/Delete'
import ImageCard from '../../components/ImageCard'
import {
  addImageAnnotationAction,
  deleteImageAnnotationAction,
  updateImageAnnotationAction,
  getImageAnnotationsAction,
  getImageVersionsAction,
} from '../../store/Workflow/actionCreator'
import { formatDateTime } from '../../helpers/dateHelper'

const ANNOTATION_WORKSPACE_HEIGHT = '740px'

const parseGeometry = (geometry = {}) => {
  if (typeof geometry !== 'string') return geometry || {}
  try {
    return JSON.parse(geometry || '{}')
  } catch {
    return {}
  }
}

export default function AnnotationWorkspace({
  data = [],
  activeImage = {},
  setActiveImage = () => {},
  finalSelectedData = [],
  onPreviewCheckChange = () => {},
  displayname = '',
  lanId = '',
  email = '',
}) {
  const dispatch = useDispatch()
  const [annotationsById, setAnnotationsById] = useState({})
  const [commentDraftByAnnotationId, setCommentDraftByAnnotationId] = useState(
    {}
  )
  const [persistedAnnotationIds, setPersistedAnnotationIds] = useState(
    new Set()
  )
  const [assetSearchTerm, setAssetSearchTerm] = useState('')
  const [versionCarouselIndex, setVersionCarouselIndex] = useState(0)
  const [commentsTabFilter, setCommentsTabFilter] = useState('active')
  const [isRevisionsVisible, setIsRevisionsVisible] = useState(false)
  const [versionsByFileName, setVersionsByFileName] = useState({})

  const activeImageKey = activeImage?.id || 'active'
  const activeFileName = activeImage?.fileName || ''

  const allAnnotations = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(annotationsById).map(([key, item = {}]) => [
          key,
          {
            ...item,
            annotations: (item.annotations || []).map(
              (annotation = {}, annotationIndex) => ({
                ...annotation,
                annotationIndex,
              })
            ),
          },
        ])
      ),
    [annotationsById]
  )

  const activeAnnotations = allAnnotations[activeImageKey]?.annotations || []
  const normalizedAssetSearch = assetSearchTerm.trim().toLowerCase()
  const filteredPreviewAssets = (data || []).filter((asset = {}) => {
    if (!normalizedAssetSearch) return true
    const fileName = `${asset.fileName || ''}`.toLowerCase()
    const id = `${asset.id || ''}`.toLowerCase()
    return (
      fileName.includes(normalizedAssetSearch) ||
      id.includes(normalizedAssetSearch)
    )
  })
  const matchingVersionAssets = useMemo(
    () =>
      (data || []).filter((asset = {}) => {
        if (!activeImage?.id && !activeImage?.fileName) return false
        return (
          (activeImage?.id && asset?.id === activeImage?.id) ||
          (activeImage?.fileName && asset?.fileName === activeImage?.fileName)
        )
      }),
    [data, activeImage?.id, activeImage?.fileName]
  )
  const versionAssets = useMemo(() => {
    const fetched = versionsByFileName[activeFileName]
    if (fetched) return fetched
    return matchingVersionAssets.length ? matchingVersionAssets : data || []
  }, [versionsByFileName, activeFileName, matchingVersionAssets, data])
  const versionCarouselSize = 5
  const getVersionAssetKey = (asset = {}, fallbackIndex = 0) =>
    `${asset?.id || asset?.fileName || 'version'}-${asset?.version || fallbackIndex}`
  const visibleVersionAssets = versionAssets.slice(
    versionCarouselIndex,
    versionCarouselIndex + versionCarouselSize
  )

  const onPreviousVersionSet = () => {
    setVersionCarouselIndex((prev) => Math.max(0, prev - 1))
  }

  const onNextVersionSet = () => {
    const maxStartIndex = Math.max(
      0,
      versionAssets.length - versionCarouselSize
    )
    setVersionCarouselIndex((prev) => Math.min(maxStartIndex, prev + 1))
  }

  const onToggleRevisions = () => {
    const nextVisibility = !isRevisionsVisible
    setIsRevisionsVisible(nextVisibility)
    if (nextVisibility && activeFileName) {
      dispatch(
        getImageVersionsAction(activeFileName, (versions, status) => {
          if (status === 'success') {
            const mapped = (versions || []).map((version = {}) => ({
              ...activeImage,
              version: version.version,
              zoomImage: version.image_zoom_url,
              previewImage: version.image_preview_url,
            }))
            setVersionsByFileName((prev) => ({
              ...prev,
              [activeFileName]: mapped,
            }))
            setVersionCarouselIndex(0)
          } else {
            setIsRevisionsVisible(false)
          }
        })
      )
    }
  }

  const getLocalDateTimeString = (value = new Date()) => {
    if (typeof value === 'string') {
      return value.length >= 19 ? value.slice(0, 19) : value
    }

    const dateValue = new Date(value)
    if (Number.isNaN(dateValue.getTime())) {
      return new Date().toISOString().slice(0, 19)
    }

    const pad = (input = 0) => `${input}`.padStart(2, '0')
    return `${dateValue.getFullYear()}-${pad(dateValue.getMonth() + 1)}-${pad(
      dateValue.getDate()
    )}T${pad(dateValue.getHours())}:${pad(dateValue.getMinutes())}:${pad(
      dateValue.getSeconds()
    )}`
  }

  const normalizeComment = (comment = {}) => {
    const resolvedAuthor =
      comment?.created_by?.display_name ||
      comment?.author ||
      displayname ||
      lanId ||
      'User'
    return {
      ...comment,
      author: resolvedAuthor,
      created_by: {
        login_id: comment?.created_by?.login_id || lanId || '',
        display_name: resolvedAuthor,
        email_address: comment?.created_by?.email_address || email || '',
      },
      created_dt: getLocalDateTimeString(
        comment.created_dt || comment.createdAt
      ),
    }
  }

  const getAnnotationComments = (annotation = {}) => {
    if (Array.isArray(annotation.comments)) {
      return annotation.comments.map((comment = {}) =>
        normalizeComment(comment)
      )
    }
    if (typeof annotation.comment === 'string' && annotation.comment.trim()) {
      return [
        normalizeComment({
          id: `${annotation.id || Date.now()}-legacy`,
          author: displayname || lanId || 'User',
          created_by: {
            login_id: lanId || '',
            display_name: displayname || lanId || 'User',
            email_address: email || '',
          },
          note: annotation.comment,
          created_dt: getLocalDateTimeString(),
        }),
      ]
    }
    return []
  }

  const normalizeAnnotation = (annotation = {}) => {
    const geometryFromLegacy = {
      left: annotation.left,
      top: annotation.top,
      width: annotation.width,
      height: annotation.height,
      points: annotation.points,
      anchorX: annotation.anchorX,
      anchorY: annotation.anchorY,
    }

    const geometry = {
      ...geometryFromLegacy,
      ...parseGeometry(annotation.geometry),
    }

    return {
      ...annotation,
      geometry,
      left: undefined,
      top: undefined,
      width: undefined,
      height: undefined,
      points: undefined,
      anchorX: undefined,
      anchorY: undefined,
    }
  }

  const updateActiveImageAnnotations = (nextAnnotations = []) => {
    const normalizedAnnotations = (nextAnnotations || []).map(
      normalizeAnnotation
    )
    const annotationIdReplacements = new Map()

    const getNormalizedComments = (annotation = {}) =>
      (annotation.comments || []).map((comment = {}) =>
        normalizeComment(comment)
      )

    const getCommentKey = (comment = {}) =>
      `${comment?.id || ''}|${comment?.note || ''}|${comment?.created_dt || ''}|${comment?.created_by?.login_id || ''}`

    const getNewlyAddedComments = (current = {}, previous = {}) => {
      const previousComments = getNormalizedComments(previous)
      const previousKeys = new Set(
        previousComments.map((comment = {}) => getCommentKey(comment))
      )
      return getNormalizedComments(current).filter(
        (comment = {}) => !previousKeys.has(getCommentKey(comment))
      )
    }

    const getComparableAnnotationPayload = (annotation = {}) => ({
      comments: getNormalizedComments(annotation),
      resolved: !!annotation.resolved,
      geometry:
        typeof annotation.geometry === 'string'
          ? annotation.geometry
          : JSON.stringify(annotation.geometry || {}),
    })

    const getResponseAnnotationId = (
      responseData = {},
      fallbackAnnotationId = ''
    ) =>
      responseData?.annotation_id ||
      responseData?.annotationId ||
      responseData?.id ||
      responseData?.data?.annotation_id ||
      responseData?.data?.annotationId ||
      responseData?.data?.id ||
      fallbackAnnotationId

    const applyAnnotationIdReplacements = (annotations = []) =>
      (annotations || []).map((annotation = {}) => {
        const mappedId = annotationIdReplacements.get(annotation.id)
        if (!mappedId) return annotation
        return {
          ...annotation,
          id: mappedId,
        }
      })

    const syncCommentDraftKeysWithReplacedIds = () => {
      if (!annotationIdReplacements.size) return

      setCommentDraftByAnnotationId((prev) => {
        const nextDrafts = { ...prev }
        annotationIdReplacements.forEach((mappedId, localId) => {
          if (Object.prototype.hasOwnProperty.call(nextDrafts, localId)) {
            nextDrafts[mappedId] = nextDrafts[localId]
            delete nextDrafts[localId]
          }
        })
        return nextDrafts
      })
    }

    const commitFinalAnnotationState = () => {
      const annotationsWithReplacedIds = applyAnnotationIdReplacements(
        normalizedAnnotations
      )
      setAnnotationsById((prev) => ({
        ...prev,
        [activeImageKey]: {
          tcin: activeImage.id,
          version: activeImage.version,
          annotations: annotationsWithReplacedIds,
        },
      }))
      clearDeletedAnnotationDrafts()
      syncCommentDraftKeysWithReplacedIds()
    }

    const activeAnnotationsById = new Map(
      activeAnnotations.map((annotation = {}) => [
        annotation.id,
        normalizeAnnotation(annotation),
      ])
    )
    const newAnnotations = []
    const updatedAnnotations = []
    const unpersisted = []
    const deletedPersistedAnnotations = activeAnnotations.filter(
      (annotation = {}) =>
        persistedAnnotationIds.has(annotation.id) &&
        !normalizedAnnotations.some(
          (nextAnnotation = {}) => nextAnnotation.id === annotation.id
        )
    )

    const clearDeletedAnnotationDrafts = () => {
      if (!deletedPersistedAnnotations.length) return

      setCommentDraftByAnnotationId((prev) => {
        const nextDrafts = { ...prev }
        deletedPersistedAnnotations.forEach((annotation = {}) => {
          delete nextDrafts[annotation.id]
        })
        return nextDrafts
      })
    }

    normalizedAnnotations.forEach((annotation = {}) => {
      const previousAnnotation = activeAnnotationsById.get(annotation.id)

      if (!previousAnnotation) {
        newAnnotations.push(annotation)
        return
      }

      const previousPayload = getComparableAnnotationPayload(previousAnnotation)
      const currentPayload = getComparableAnnotationPayload(annotation)
      const isChanged =
        JSON.stringify(previousPayload) !== JSON.stringify(currentPayload)

      if (!isChanged) return

      if (persistedAnnotationIds.has(annotation.id)) {
        updatedAnnotations.push({
          current: annotation,
          previous: previousAnnotation,
        })
      } else {
        unpersisted.push(annotation)
      }
    })

    if (!activeImage?.fileName) {
      setAnnotationsById((prev) => ({
        ...prev,
        [activeImageKey]: {
          tcin: activeImage.id,
          version: activeImage.version,
          annotations: normalizedAnnotations,
        },
      }))
      return
    }

    let successCount = 0
    const totalApiCalls =
      newAnnotations.length +
      updatedAnnotations.length +
      unpersisted.length +
      deletedPersistedAnnotations.length

    // Add new annotations via API
    newAnnotations.forEach((annotation = {}) => {
      const payload = {
        image_name: activeImage.fileName,
        version: activeImage.version,
        ...annotation,
        comments: (annotation.comments || []).map((comment = {}) =>
          normalizeComment(comment)
        ),
        geometry:
          typeof annotation.geometry === 'string'
            ? annotation.geometry
            : JSON.stringify(annotation.geometry || {}),
      }
      dispatch(
        addImageAnnotationAction(payload, (data, status) => {
          if (status === 'success') {
            successCount += 1
            const resolvedAnnotationId = getResponseAnnotationId(
              data,
              annotation.id
            )
            if (resolvedAnnotationId !== annotation.id) {
              annotationIdReplacements.set(annotation.id, resolvedAnnotationId)
            }
            setPersistedAnnotationIds((prev) => {
              const nextIds = new Set(prev)
              nextIds.delete(annotation.id)
              nextIds.add(resolvedAnnotationId)
              return nextIds
            })
            if (successCount === totalApiCalls) {
              commitFinalAnnotationState()
            }
          }
        })
      )
    })

    // Add unpersisted annotations (created locally, not yet sent to server) via API
    unpersisted.forEach((annotation = {}) => {
      const payload = {
        image_name: activeImage.fileName,
        version: activeImage.version,
        ...annotation,
        comments: (annotation.comments || []).map((comment = {}) =>
          normalizeComment(comment)
        ),
        geometry:
          typeof annotation.geometry === 'string'
            ? annotation.geometry
            : JSON.stringify(annotation.geometry || {}),
      }
      dispatch(
        addImageAnnotationAction(payload, (data, status) => {
          if (status === 'success') {
            successCount += 1
            const resolvedAnnotationId = getResponseAnnotationId(
              data,
              annotation.id
            )
            if (resolvedAnnotationId !== annotation.id) {
              annotationIdReplacements.set(annotation.id, resolvedAnnotationId)
            }
            setPersistedAnnotationIds((prev) => {
              const nextIds = new Set(prev)
              nextIds.delete(annotation.id)
              nextIds.add(resolvedAnnotationId)
              return nextIds
            })
            if (successCount === totalApiCalls) {
              commitFinalAnnotationState()
            }
          }
        })
      )
    })

    // Update existing persisted annotations (e.g., with new comments) via API
    updatedAnnotations.forEach((item = {}) => {
      const annotation = item.current || {}
      const previousAnnotation = item.previous || {}
      const basePayload = getComparableAnnotationPayload(annotation)
      const payload = {
        ...basePayload,
        comments: getNewlyAddedComments(annotation, previousAnnotation),
      }
      dispatch(
        updateImageAnnotationAction(annotation.id, payload, (data, status) => {
          if (status === 'success') {
            successCount += 1
            if (successCount === totalApiCalls) {
              commitFinalAnnotationState()
            }
          }
        })
      )
    })

    // Delete annotations removed from the canvas (hover popup delete, etc.)
    deletedPersistedAnnotations.forEach((annotation = {}) => {
      dispatch(
        deleteImageAnnotationAction(annotation.id, (data, status) => {
          if (status === 'success') {
            successCount += 1
            setPersistedAnnotationIds((prev) => {
              const nextIds = new Set(prev)
              nextIds.delete(annotation.id)
              return nextIds
            })
            if (successCount === totalApiCalls) {
              commitFinalAnnotationState()
            }
          }
        })
      )
    })

    // If no API calls needed, update state immediately
    if (totalApiCalls === 0) {
      commitFinalAnnotationState()
    }
  }

  const toggleAnnotationResolved = (annotationId) => {
    const nextAnnotations = activeAnnotations.map((item = {}) =>
      item.id === annotationId ? { ...item, resolved: !item.resolved } : item
    )
    updateActiveImageAnnotations(nextAnnotations)
  }

  const deleteAnnotationFromPanel = (annotationId) => {
    if (persistedAnnotationIds.has(annotationId)) {
      dispatch(
        deleteImageAnnotationAction(annotationId, (data, status) => {
          if (status === 'success') {
            setPersistedAnnotationIds((prev) => {
              const nextIds = new Set(prev)
              nextIds.delete(annotationId)
              return nextIds
            })
            setAnnotationsById((prev) => ({
              ...prev,
              [activeImageKey]: {
                ...prev[activeImageKey],
                annotations: (prev[activeImageKey]?.annotations || []).filter(
                  (ann = {}) => ann.id !== annotationId
                ),
              },
            }))
            setCommentDraftByAnnotationId((prev) => {
              const nextDrafts = { ...prev }
              delete nextDrafts[annotationId]
              return nextDrafts
            })
          }
        })
      )
    } else {
      setAnnotationsById((prev) => ({
        ...prev,
        [activeImageKey]: {
          ...prev[activeImageKey],
          annotations: (prev[activeImageKey]?.annotations || []).filter(
            (ann = {}) => ann.id !== annotationId
          ),
        },
      }))
      setCommentDraftByAnnotationId((prev) => {
        const nextDrafts = { ...prev }
        delete nextDrafts[annotationId]
        return nextDrafts
      })
    }
  }

  const addResolutionComment = (annotationId) => {
    const nextText = (commentDraftByAnnotationId[annotationId] || '').trim()
    if (!nextText) return

    const nextAnnotations = activeAnnotations.map((item = {}) => {
      if (item.id !== annotationId) return item
      const existingComments = getAnnotationComments(item)
      return {
        ...item,
        comments: [
          ...existingComments,
          normalizeComment({
            id: `${Date.now()}-${existingComments.length}`,
            author: displayname || lanId || 'User',
            created_by: {
              login_id: lanId || '',
              display_name: displayname || lanId || 'User',
              email_address: email || '',
            },
            note: nextText,
            created_dt: getLocalDateTimeString(),
          }),
        ],
      }
    })

    updateActiveImageAnnotations(nextAnnotations)
    setCommentDraftByAnnotationId((prev) => ({
      ...prev,
      [annotationId]: '',
    }))
  }

  useEffect(() => {
    if (!activeImage?.fileName) return
    const version = activeImage?.version || ''

    dispatch(
      getImageAnnotationsAction(
        activeImage.fileName,
        version,
        (annotations = [], status = '') => {
          if (status !== 'success') return
          const normalizedAnnotations = (annotations || []).map(
            normalizeAnnotation
          )
          setAnnotationsById((prev) => ({
            ...prev,
            [activeImageKey]: {
              tcin: activeImage.id,
              version,
              annotations: normalizedAnnotations,
            },
          }))
          const persistedIds = new Set(
            normalizedAnnotations.map((annotation = {}) => annotation.id)
          )
          setPersistedAnnotationIds(persistedIds)
        }
      )
    )
  }, [
    dispatch,
    activeImageKey,
    activeImage?.fileName,
    activeImage?.id,
    activeImage?.version,
  ])

  useEffect(() => {
    if (!versionAssets.length) {
      setVersionCarouselIndex(0)
      return
    }

    const activeIndex = versionAssets.findIndex(
      (asset = {}) =>
        asset?.id === activeImage?.id &&
        asset?.fileName === activeImage?.fileName &&
        asset?.version === activeImage?.version
    )

    if (activeIndex === -1) {
      setVersionCarouselIndex(0)
      return
    }

    if (activeIndex < versionCarouselIndex) {
      setVersionCarouselIndex(activeIndex)
      return
    }

    if (activeIndex >= versionCarouselIndex + versionCarouselSize) {
      setVersionCarouselIndex(activeIndex - versionCarouselSize + 1)
    }
  }, [activeImage, versionAssets, versionCarouselIndex])

  useEffect(() => {
    setIsRevisionsVisible(false)
  }, [activeImageKey])

  const activeRevisionLabel = activeImage?.version
    ? `${activeImage.version}`
    : 'v1'
  const activeRevisionStatus =
    activeAnnotations.length === 0
      ? 'No Reviews'
      : activeAnnotations.some((annotation = {}) => !annotation.resolved)
        ? 'Active'
        : 'Resolved'

  const onOpenActiveImage = () => {
    const imageUrl = activeImage?.zoomImage || activeImage?.previewImage
    if (!imageUrl) return
    window.open(imageUrl, '_blank')
  }

  return (
    <Grid
      container
      wrap="nowrap"
      spacing={2}
      style={{
        width: '100%',
        padding: '16px 24px 24px',
        marginTop: '8px',
        alignItems: 'flex-start',
      }}
    >
      <Grid
        item
        style={{
          width: '300px',
          minWidth: '300px',
          maxWidth: '300px',
        }}
      >
        <Box
          style={{
            height: ANNOTATION_WORKSPACE_HEIGHT,
            boxSizing: 'border-box',
            overflowY: 'auto',
            border: '1px solid #d7dbe4',
            borderRadius: '8px',
            padding: '14px',
            background: '#f4f5f7',
          }}
        >
          <Typography
            style={{
              fontWeight: 600,
              marginBottom: '8px',
            }}
          >
            Images ({filteredPreviewAssets.length})
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search Images..."
            value={assetSearchTerm}
            onChange={(event) => {
              setAssetSearchTerm(event.target.value || '')
            }}
            style={{ marginBottom: '12px', background: '#fff' }}
          />
          {(filteredPreviewAssets || []).map((dat) => {
            const isActive = activeImage.id === dat.id
            const isSelected = finalSelectedData.some(
              (item) => item.id === dat.id
            )

            return (
              <div
                key={dat?.id || ''}
                style={{
                  padding: '14px',
                  marginBottom: '14px',
                  cursor: 'pointer',
                  background: '#fff',
                  border: isActive ? '2px solid #CC0000' : '1px solid #bfc5d1',
                  borderRadius: '6px',
                }}
                onClick={() => {
                  setActiveImage(dat)
                }}
              >
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '8px',
                    gap: '8px',
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={(event) => {
                      event.stopPropagation()
                      onPreviewCheckChange(dat)
                    }}
                    style={{
                      padding: '0px',
                      marginRight: '4px',
                    }}
                  />
                  <Typography
                    style={{
                      fontSize: '12px',
                      fontWeight: 700,
                      color: '#2f3137',
                      lineHeight: 1.35,
                      wordBreak: 'break-word',
                    }}
                  >
                    {dat.fileName || dat.id || ''}
                  </Typography>
                </Box>
                <img
                  alt={dat.id}
                  style={{
                    display: 'block',
                    height: '120px',
                    width: '100%',
                    objectFit: 'contain',
                    background: '#fff',
                    border: '1px solid #c5c5c5',
                    borderRadius: '2px',
                  }}
                  src={dat.previewImage}
                />
                <Typography
                  style={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#2f3137',
                    marginTop: '8px',
                  }}
                >
                  Revision: {dat.version || 1}
                </Typography>
              </div>
            )
          })}
        </Box>
      </Grid>

      <Grid item style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            width: '100%',
            height: ANNOTATION_WORKSPACE_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Box
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              background: '#fff',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
            }}
          >
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 16px',
                borderBottom: '1px solid #ebedf0',
                background: '#ffffff',
                gap: '12px',
              }}
            >
              <Box style={{ minWidth: 0 }}>
                <Typography
                  style={{
                    fontSize: '18px',
                    fontWeight: 700,
                    color: '#111827',
                    lineHeight: 1.2,
                    marginBottom: '4px',
                    wordBreak: 'break-word',
                  }}
                >
                  {activeImage?.fileName || activeImage?.id || '-'}
                </Typography>
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <Typography
                    style={{
                      fontSize: '14px',
                      color: '#4b5563',
                      fontWeight: 500,
                    }}
                  >
                    Revision: {activeRevisionLabel}
                  </Typography>
                  <Box
                    style={{
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      background:
                        activeRevisionStatus === 'No Reviews'
                          ? '#f3f4f6'
                          : activeRevisionStatus === 'Active'
                            ? '#fff3f3'
                            : '#ecfdf3',
                      color:
                        activeRevisionStatus === 'No Reviews'
                          ? '#4b5563'
                          : activeRevisionStatus === 'Active'
                            ? '#CC0000'
                            : '#15803d',
                      fontSize: '13px',
                      fontWeight: 600,
                      padding: '2px 8px',
                    }}
                  >
                    ({activeRevisionStatus})
                  </Box>
                </Box>
              </Box>

              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                <IconButton
                  onClick={onOpenActiveImage}
                  disabled={
                    !activeImage?.zoomImage && !activeImage?.previewImage
                  }
                  size="small"
                  title="Open Image"
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    width: '36px',
                    height: '36px',
                    background: '#fff',
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box style={{ flex: 1, minHeight: 0 }}>
              <ImageCard
                title=""
                imageSrc={activeImage.zoomImage}
                zoomSrc={activeImage.zoomImage}
                userDisplayName={displayname}
                enableAnnotations
                annotations={activeAnnotations}
                onAnnotationsChange={updateActiveImageAnnotations}
                annotationViewportHeight="100%"
                annotationViewportStyle={{
                  border: '1px solid #e5e7eb',
                  background: '#f7f8fa',
                }}
              />
            </Box>
          </Box>

          <Box
            style={{
              minHeight: isRevisionsVisible ? '132px' : '56px',
              maxHeight: 'auto',
              border: '1px solid #d0d0d0',
              background: '#ffffff',
              padding: '10px 12px',
              boxSizing: 'border-box',
            }}
          >
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: isRevisionsVisible ? '8px' : 0,
              }}
            >
              <Typography
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#2f3137',
                }}
              >
                Revisions
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={onToggleRevisions}
                style={{
                  textTransform: 'none',
                  fontSize: '12px',
                  fontWeight: 600,
                  minWidth: '120px',
                  padding: '2px 10px',
                }}
              >
                {isRevisionsVisible ? 'Hide Revisions' : 'Show Revisions'}
              </Button>
            </Box>

            {isRevisionsVisible && (
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <IconButton
                  onClick={onPreviousVersionSet}
                  disabled={versionCarouselIndex === 0}
                  size="small"
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '1px solid #d0d0d0',
                    borderRadius: '50%',
                    background: '#fff',
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                  style={{
                    display: 'flex',
                    gap: '10px',
                    flex: 1,
                    overflow: 'hidden',
                    alignItems: 'stretch',
                  }}
                >
                  {visibleVersionAssets.map((asset = {}, index) => {
                    const absoluteIndex = versionCarouselIndex + index
                    const activeAssetKey = getVersionAssetKey(activeImage)
                    const assetKey = getVersionAssetKey(asset, absoluteIndex)
                    const isCurrent = activeAssetKey === assetKey
                    const rawVersion = asset?.version || absoluteIndex + 1
                    const versionLabel = `${rawVersion}`
                      .toLowerCase()
                      .startsWith('v')
                      ? `${rawVersion}`
                      : `v${rawVersion}`

                    return (
                      <Box
                        key={assetKey}
                        onClick={() => setActiveImage(asset)}
                        style={{
                          width: '128px',
                          minWidth: '128px',
                          border: isCurrent
                            ? '2px solid #CC0000'
                            : '1px solid #d8d8d8',
                          borderRadius: '6px',
                          background: '#fff',
                          cursor: 'pointer',
                          padding: '8px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <img
                          alt={
                            asset?.id || asset?.fileName || 'version-thumbnail'
                          }
                          src={asset?.previewImage}
                          style={{
                            width: '100%',
                            height: '54px',
                            objectFit: 'contain',
                            display: 'block',
                            marginBottom: '6px',
                          }}
                        />
                        <Typography
                          style={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: isCurrent ? '#CC0000' : '#2f3137',
                          }}
                        >
                          {`${versionLabel}${isCurrent ? ' (Current)' : ''}`}
                        </Typography>
                      </Box>
                    )
                  })}
                </Box>

                <IconButton
                  onClick={onNextVersionSet}
                  disabled={
                    versionCarouselIndex + versionCarouselSize >=
                    versionAssets.length
                  }
                  size="small"
                  style={{
                    width: '32px',
                    height: '32px',
                    border: '1px solid #d0d0d0',
                    borderRadius: '50%',
                    background: '#fff',
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {!isRevisionsVisible && (
              <Typography
                style={{
                  fontSize: '12px',
                  color: '#6f7683',
                  marginTop: '4px',
                }}
              >
                {versionsByFileName[activeFileName] &&
                versionAssets.length === 0
                  ? 'No revisions available'
                  : 'Click "Show Revisions" to load versions'}
              </Typography>
            )}
          </Box>
        </div>
      </Grid>

      <Grid
        item
        style={{
          width: '340px',
          minWidth: '340px',
          maxWidth: '340px',
        }}
      >
        <Box
          style={{
            height: ANNOTATION_WORKSPACE_HEIGHT,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid #e0e0e0',
            borderRadius: '12px',
            background: '#ffffff',
            overflow: 'hidden',
          }}
        >
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '14px 16px 10px',
              borderBottom: '1px solid #f0f0f0',
              flexShrink: 0,
            }}
          >
            <Typography
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: '#1a1a1a',
              }}
            >
              Comments & Resolution
            </Typography>
          </Box>

          {(() => {
            const activeCount = activeAnnotations.filter(
              (annotation) => !annotation.resolved
            ).length
            const resolvedCount = activeAnnotations.filter(
              (annotation) => annotation.resolved
            ).length
            const tabs = [
              {
                key: 'active',
                label: 'Active',
                count: activeCount,
                activeColor: '#1565c0',
                badgeBg: '#1565c0',
              },
              {
                key: 'resolved',
                label: 'Resolved',
                count: resolvedCount,
                activeColor: '#2e7d32',
                badgeBg: '#388e3c',
              },
            ]
            return (
              <Box
                style={{
                  display: 'flex',
                  gap: '4px',
                  padding: '10px 12px 0',
                  borderBottom: '1px solid #f0f0f0',
                  flexShrink: 0,
                }}
              >
                {tabs.map((tab) => {
                  const isActive = commentsTabFilter === tab.key
                  return (
                    <Box
                      key={tab.key}
                      onClick={() => setCommentsTabFilter(tab.key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 10px',
                        cursor: 'pointer',
                        borderBottom: isActive
                          ? `2px solid ${tab.activeColor}`
                          : '2px solid transparent',
                        marginBottom: '-1px',
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: '13px',
                          fontWeight: isActive ? 700 : 400,
                          color: isActive ? tab.activeColor : '#555',
                        }}
                      >
                        {tab.label}
                      </Typography>
                      <Box
                        style={{
                          minWidth: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: isActive ? tab.badgeBg : '#e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: isActive ? '#fff' : '#666',
                          }}
                        >
                          {tab.count}
                        </Typography>
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )
          })()}

          <Box
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {activeAnnotations.length === 0 && (
              <Typography
                style={{
                  color: '#aaa',
                  fontSize: '14px',
                  textAlign: 'center',
                  marginTop: '32px',
                }}
              >
                No annotations yet
              </Typography>
            )}

            {activeAnnotations
              .filter((annotation = {}) => {
                if (commentsTabFilter === 'resolved') {
                  return !!annotation.resolved
                }
                return !annotation.resolved
              })
              .map((annotation = {}, index) => {
                const colorPalette = [
                  '#e53935',
                  '#1e88e5',
                  '#43a047',
                  '#fb8c00',
                  '#8e24aa',
                ]
                const dotColor = annotation.resolved
                  ? '#43a047'
                  : colorPalette[index % colorPalette.length]
                const annotationComments = getAnnotationComments(annotation)
                const isResolved = !!annotation.resolved

                return (
                  <Box
                    key={annotation.id || index}
                    style={{
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px',
                      background: '#ffffff',
                      display: 'flex',
                      flexDirection: 'column',
                      borderLeft: `4px solid ${dotColor}`,
                    }}
                  >
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '10px 10px 6px',
                      }}
                    >
                      <Box
                        style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: dotColor,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        style={{
                          fontSize: '14px',
                          fontWeight: 700,
                          color: '#1a1a1a',
                          marginRight: '2px',
                        }}
                      >
                        #{annotation.annotationIndex + 1}
                      </Typography>
                      <Box style={{ flex: 1 }} />
                      <Box
                        style={{
                          padding: '2px 10px',
                          borderRadius: '12px',
                          background: isResolved ? '#e8f5e9' : '#fff3e0',
                          border: `1px solid ${isResolved ? '#a5d6a7' : '#ffcc80'}`,
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: isResolved ? '#2e7d32' : '#e65100',
                          }}
                        >
                          {isResolved ? 'Resolved' : 'Active'}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => deleteAnnotationFromPanel(annotation.id)}
                        style={{ padding: '2px', color: '#cc0000' }}
                        title="Delete annotation"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <Checkbox
                        checked={isResolved}
                        onChange={() => toggleAnnotationResolved(annotation.id)}
                        size="small"
                        style={{ padding: '2px' }}
                      />
                    </Box>

                    {annotation.description && (
                      <Typography
                        style={{
                          fontSize: '13px',
                          color: '#333',
                          padding: '0 12px 8px',
                          lineHeight: 1.4,
                        }}
                      >
                        {annotation.description}
                      </Typography>
                    )}

                    {annotationComments.length > 0 && (
                      <Box
                        style={{
                          margin: '0 10px 8px',
                          borderRadius: '6px',
                          background: '#f3f6fb',
                          padding: '6px 8px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                        }}
                      >
                        {annotationComments.map((entry = {}) => {
                          const authorName =
                            entry?.created_by?.display_name ||
                            entry?.author ||
                            'User'
                          const initials = authorName
                            .split(' ')
                            .map((word) => word[0])
                            .join('')
                            .substring(0, 2)
                            .toUpperCase()
                          const avatarColors = [
                            '#5c6bc0',
                            '#26a69a',
                            '#ef5350',
                            '#ab47bc',
                            '#ff7043',
                          ]
                          const avatarColor =
                            avatarColors[
                              authorName.charCodeAt(0) % avatarColors.length
                            ]
                          const timeLabel = entry.created_dt
                            ? formatDateTime(
                                entry.created_dt,
                                'MMM D, YYYY - h:mm'
                              )
                            : ''
                          return (
                            <Box
                              key={entry.id}
                              style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Box
                                style={{
                                  width: '28px',
                                  height: '28px',
                                  borderRadius: '50%',
                                  background: avatarColor,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <Typography
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    color: '#fff',
                                  }}
                                >
                                  {initials}
                                </Typography>
                              </Box>
                              <Box style={{ flex: 1 }}>
                                <Box
                                  style={{
                                    display: 'flex',
                                    gap: '4px',
                                    alignItems: 'baseline',
                                  }}
                                >
                                  <Typography
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: 700,
                                      color: '#1a1a1a',
                                    }}
                                  >
                                    {authorName}
                                  </Typography>
                                  {timeLabel && (
                                    <Typography
                                      style={{
                                        fontSize: '11px',
                                        color: '#999',
                                      }}
                                    >
                                      · {timeLabel}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography
                                  style={{
                                    fontSize: '12px',
                                    color: '#444',
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {entry.note}
                                </Typography>
                              </Box>
                            </Box>
                          )
                        })}
                      </Box>
                    )}

                    {isResolved && (
                      <Box
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 12px 8px',
                        }}
                      >
                        <Box
                          style={{
                            width: '18px',
                            height: '18px',
                            borderRadius: '50%',
                            background: '#43a047',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Typography
                            style={{
                              fontSize: '10px',
                              color: '#fff',
                              fontWeight: 700,
                            }}
                          >
                            ✓
                          </Typography>
                        </Box>
                        <Typography
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#2e7d32',
                          }}
                        >
                          Resolved
                        </Typography>
                      </Box>
                    )}

                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        margin: '0 10px 10px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '20px',
                        padding: '4px 8px 4px 12px',
                        background: '#fff',
                      }}
                    >
                      <input
                        placeholder="Add a comment..."
                        value={commentDraftByAnnotationId[annotation.id] || ''}
                        onChange={(event) => {
                          const { value = '' } = event.target || {}
                          setCommentDraftByAnnotationId((prev) => ({
                            ...prev,
                            [annotation.id]: value,
                          }))
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            addResolutionComment(annotation.id)
                          }
                        }}
                        style={{
                          flex: 1,
                          border: 'none',
                          outline: 'none',
                          fontSize: '12px',
                          color: '#333',
                          background: 'transparent',
                          padding: '2px 0',
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => addResolutionComment(annotation.id)}
                        style={{ padding: '4px' }}
                      >
                        <SendIcon
                          style={{
                            fontSize: '16px',
                            color: '#1565c0',
                            transform: 'rotate(-45deg)',
                          }}
                        />
                      </IconButton>
                    </Box>
                  </Box>
                )
              })}
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}
